import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { cn } from '@/lib/cn';

/**
 * Emeraude's footer grid, by how many link columns stand beside the wordmark.
 * Written out because Tailwind reads class names out of the source and never
 * sees a string built at runtime.
 */
const EMERAUDE_FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[2fr_1fr]',
  2: 'lg:grid-cols-[2fr_1fr_1fr]',
  3: 'lg:grid-cols-[2fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]',
};

/** The footer's copy colour: ivory, read against the emerald ground. */
const IVORY = '#f4f1ea';

/** One footer column: a serif heading over its links. */
function EmeraudeColumn({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-4 text-[1.7rem] leading-tight"
        style={{ fontFamily: 'var(--storefront-heading-font)', color: IVORY }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The emerald close to a white page.
 *
 * The footer is set on the merchant's primary, so it moves with their palette
 * - which is also why its copy is a fixed ivory rather than the page colour: a
 * house that repaints its background cream still wants its footer copy light.
 * Wordmark and the shop's line on the left, then the footer menu, the shelves
 * and the pages, each skipped when empty and the grid sized to what is left.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('header.menu_categories')
      : shopTitle;

  return (
    <footer
      className="pt-16 text-[14px] tracking-[0.02em]"
      style={{ backgroundColor: 'var(--storefront-primary)', color: `${IVORY}d9` }}
    >
      <StorefrontContainer
        className={cn('grid gap-10 pb-14 text-start sm:grid-cols-2', EMERAUDE_FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-4xl uppercase leading-none tracking-[0.06em]"
            style={{ fontFamily: 'var(--storefront-heading-font)', color: IVORY }}
          >
            {storeName}
          </p>
          {description ? <p className="mt-5 max-w-[24rem] leading-relaxed">{description}</p> : null}
        </div>
        {items.length > 0 ? (
          <EmeraudeColumn title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-2.5">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:text-white hover:underline"
                />
              ))}
            </nav>
          </EmeraudeColumn>
        ) : null}
        {shelfColumns.map((column, index) => (
          <EmeraudeColumn key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-2.5"
            >
              {column.map((category) => (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  className="hover:text-white hover:underline"
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </EmeraudeColumn>
        ))}
        {preview ? null : (
          <EmeraudeColumn title={copy('theme.footer_support')}>
            <PageLinks className="mb-2.5 flex-col items-start justify-start gap-2.5" />
            <BlogLinks className="flex-col items-start justify-start gap-2.5" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </EmeraudeColumn>
        )}
      </StorefrontContainer>
      <StorefrontContainer>
        <div
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t py-6 text-[12px]"
          style={{ borderColor: `${IVORY}40` }}
        >
          <LanguageSwitcherSlot placement="footer" preview={preview} />
          <span style={copyrightStyle(footer)}>
            © {new Date().getFullYear()} {storeName} ·{' '}
            {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </div>
      </StorefrontContainer>
    </footer>
  );
};
